import { useState, useEffect } from "react";
import api from "../../../services/api.service";

/**
 * Formats a moment/Date to "YYYY-MM-DD".
 */
const toDateStr = (d) => {
  // Accept both moment objects and native Date
  if (d && typeof d.format === "function") return d.format("YYYY-MM-DD");
  return new Date(d).toISOString().slice(0, 10);
};

/**
 * Fetches one page of company score data.
 * Returns the first element of the array response, or null.
 */
const fetchCompanyScore = async (dateI, dateF, diseaseName) => {
  const params = {
    dateI,
    dateF,
    ...(diseaseName ? { enfermedad: diseaseName } : {}),
  };
  const response = await api.get("/healthScore/companyscore", {
    params,
  });
  const result = Array.isArray(response.data)
    ? response.data[0]
    : response.data;
  return result ?? null;
};

/**
 * Hook to fetch the company health score for a given date range.
 *
 * @param {Array} dateRange  [momentStart, momentEnd] from the dashboard filter.
 *                           When omitted, falls back to current-month / previous-month.
 *
 * Returns:
 *   data        – score object for the selected range  { KPI_Final, Avg_Score_Sueno, … }
 *   prevData    – score object for the equivalent previous period (same duration, shifted back)
 *   scoreDelta  – formatted string like "+3.2", "-1.5", "0.0"
 *   loading     – true while either request is in flight
 *   error       – error object if any request failed
 */
const useCompanyScore = (dateRange, diseaseName = "") => {
  const [data, setData] = useState(null);
  const [prevData, setPrevData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Derive stable string keys so the effect only re-runs when dates actually change
  const startKey = dateRange?.[0] ? toDateStr(dateRange[0]) : null;
  const endKey   = dateRange?.[1] ? toDateStr(dateRange[1]) : null;

  useEffect(() => {
    const fetchBoth = async () => {
      setLoading(true);
      setError(null);

      try {
        let currentDateI, currentDateF, prevDateI, prevDateF;

        if (startKey && endKey) {
          // ── Use the filter's date range ──────────────────────────────────
          currentDateI = startKey;
          currentDateF = endKey;

          // Previous period = same duration, shifted back by the same number of days
          const start    = new Date(startKey);
          const end      = new Date(endKey);
          const duration = end - start; // ms
          const prevEnd  = new Date(start.getTime() - 1);          // day before start
          const prevStart = new Date(prevEnd.getTime() - duration); // same span back

          prevDateI = prevStart.toISOString().slice(0, 10);
          prevDateF = prevEnd.toISOString().slice(0, 10);
        } else {
          // ── Fallback: current month vs previous month ─────────────────────
          const today = new Date();

          currentDateF = today.toISOString().slice(0, 10);
          currentDateI = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;

          const lastDayPrev = new Date(today.getFullYear(), today.getMonth(), 0);
          prevDateF = lastDayPrev.toISOString().slice(0, 10);
          prevDateI = `${lastDayPrev.getFullYear()}-${String(lastDayPrev.getMonth() + 1).padStart(2, "0")}-01`;
        }

        // ── Parallel fetch ─────────────────────────────────────────────────
        const [current, prev] = await Promise.all([
          fetchCompanyScore(currentDateI, currentDateF, diseaseName),
          fetchCompanyScore(prevDateI,    prevDateF, diseaseName),
        ]);

        setData(current);
        setPrevData(prev);
      } catch (err) {
        console.error("useCompanyScore fetch error:", err);
        setError(err);
        setData(null);
        setPrevData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBoth();
  }, [startKey, endKey, diseaseName]); // re-fetch whenever the filter date range or disease changes

  // ── Derive delta ──────────────────────────────────────────────────────────
  // Positive → selected period is better than previous (improvement)
  // Negative → selected period is lower (regression)
  const scoreDelta = (() => {
    const current  = data?.KPI_Final;
    const previous = prevData?.KPI_Final;
    if (current == null || previous == null) return null;
    const diff = current - previous;
    const sign = diff >= 0 ? "+" : "";
    return `${sign}${diff.toFixed(1)}`;
  })();

  return { data, prevData, scoreDelta, loading, error };
};

export default useCompanyScore;
